import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api";
import PasswordInput from "../components/PasswordInput";
import { useBreadcrumb } from "../hooks/useBreadcrumb";

const INPUT = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid var(--border)",
  borderRadius: "10px",
  color: "var(--text-primary)",
  fontSize: "14px",
  outline: "none",
};


function Field({ label, value, onChange, placeholder, type = "text", autoComplete, readOnly }) {
  const inputProps = {
    value,
    onChange: (e) => onChange && onChange(e.target.value),
    placeholder,
    autoComplete,
    readOnly,
    className: "form__input",
  };

  return (
    <div className="wrapper__form__col w__100">
      <label className="form__label">{label}</label>
      {type === "password" ? (
        <PasswordInput {...inputProps} />
      ) : (
        <input type={type} {...inputProps} />
      )}
    </div>
  );
}

export default function ProfilePage({ auth }) {
  const navigate = useNavigate();
  const user = auth.user;
  useBreadcrumb([
    { label: "Workspace", to: "/mainScreen" },
    { label: "Profile" },
  ]);
  const [form, setForm] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.newPassword) {
      if (!form.currentPassword) {
        toast.error("Please enter your current password to set a new one");
        return;
      }
      if (form.newPassword.length < 6) {
        toast.error("New password must be at least 6 characters");
        return;
      }
      if (form.newPassword === form.currentPassword) {
        toast.error("New password must be different from your current password");
        return;
      }
      if (form.newPassword !== form.confirmPassword) {
        toast.error("New passwords do not match");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber,
      };
      if (form.newPassword) {
        payload.password = form.newPassword;
        payload.currentPassword = form.currentPassword;
      }

      const res = await api.updateProfile(user.id, payload);
      auth.updateUser({
        fullName: res.data.user.fullName,
        email: res.data.user.email,
        phoneNumber: res.data.user.phoneNumber,
      });
      toast.success("Profile updated successfully");
      setForm((f) => ({ ...f, currentPassword: "", newPassword: "", confirmPassword: "" }));
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const displayName = user?.fullName || user?.username || "";
  const initial = displayName.split(" ").map((n) => n[0]).join("").toUpperCase();

  const roleClass = user?.role === "admin" ? "role__badge__admin" : user?.role === "subadmin" ? "role__badge__subadmin" : "";

  return (
    <div className="wrapper__pofile wrapper__main">
      <div className="wrapper__pofile__inner mw__672">
        <div className="wrapper__nav mb__16">
          <nav>
            <ul>
              <li>
                <button onClick={() => navigate(-1)} className="wrapper__back__link fb__13 fw__500 fc__5F636A">
                  <img src="/svg/back.svg" alt="Back " />
                  back
                </button>
              </li>
            </ul>
          </nav>
        </div>
        {/* Avatar + name header */}
        <div className="profile__details d-flex align-items-center mb__16 gap__16">
          <div className="username__initial">
            {initial}
          </div>
          <div>
            <h1 className="h3 fc__0E1218 fw__700 mb__10 display__name">
              {displayName}
            </h1>
            <div className="user__details d-flex align-items-center gap__8">
              <span className="fb__13 fc__5F636A fw__500">
                @{user?.username}
              </span>
              <span className={`role__badge ${roleClass}`}>
                {user?.role}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Personal info card */}
          <div className="wrapper__profile__card mb__24">
            <p className="fb__12 fw__700 uppercase mb__20 fc__5F636A">Personal Information</p>
            <div className="wrapper__form__row  custom__row gap__16">
              <Field
                label="Username"
                value={user?.username || ""}
                readOnly
                placeholder="Username"
              />
              <Field
                label="Full Name"
                value={form.fullName}
                onChange={(v) => setForm((f) => ({ ...f, fullName: v }))}
                placeholder="Your full name"
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                placeholder="email@example.com"
              />
              <Field
                label="Phone Number"
                value={form.phoneNumber}
                onChange={(v) => setForm((f) => ({ ...f, phoneNumber: v }))}
                placeholder="+1 555 000 0000"
              />
            </div>
          </div>

          {/* Password card */}
          <div className="wrapper__profile__card mb__24">
            <p className="fb__12 fw__700 uppercase mb__20 fc__5F636A">
              Change Password
            </p>
            <div className="wrapper__form__row  custom__row gap__16">
              <Field
                label="New Password"
                type="password"
                value={form.newPassword}
                onChange={(v) => setForm((f) => ({ ...f, newPassword: v }))}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
              {form.newPassword.length > 0 && (
                <>
                  <Field
                    label="Confirm New Password"
                    type="password"
                    value={form.confirmPassword}
                    onChange={(v) => setForm((f) => ({ ...f, confirmPassword: v }))}
                    placeholder="Repeat new password"
                    autoComplete="new-password"
                  />
                  <Field
                    label="Current Password"
                    type="password"
                    value={form.currentPassword}
                    onChange={(v) => setForm((f) => ({ ...f, currentPassword: v }))}
                    placeholder="Enter your current password to confirm"
                    autoComplete="current-password"
                  />
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="wrapper__submit d-flex align-items-center flex-end gap__8">
            <button type="button" onClick={() => navigate(-1)} className="common__btn common__transp__btn">
              Cancel
            </button>
            <button type="submit" className="common__btn common__blk__btn" disabled={saving} >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
